// functions/home_page_fetch_functions.js
import { sortList } from "../../components/lib/functions";
import logger from "../../utils/logger";

// Import query routes
import { fetch_all_lanes, fetch_all_warehouse_costs, fetchOrders } from "../../api/warehouse";
import { fetch_all_profiles } from "../../api/profiles";
import { fetch_all_vehicles } from "../../api/vehicles";
import { fetch_all_service_records } from "../../api/service";
import { fetch_all_customers, fetch_all_customer_deliveries } from "../../api/customers";
import { fetch_todays_defects, fetch_outstanding_defects } from "../../api/defects";
import { fetch_dashboard_statistics } from '../../api/dashboard';
import { fetch_pending_holiday_requests, fetch_pending_holiday_requests_count } from '../../api/holidayRequests';

export class HomePageFetchData {

    constructor(showAlert) {
        this.showAlert = showAlert;
    }

    getEmptyDashboardData = () => ({
        dailyJobsCompleted: null,
        jobStats: {
            thisWeek: 0,
            thisMonth: 0,
            subbed: 0
        },
        serviceStats: null,
        warehouseStock: null,
        orderCounts: {
            pending: 0,
            picked: 0,
            thisWeek: 0
        },
        defectKpis: {
            period28Days: 0,
            outstanding: 0,
            completionRate: 100
        },
        tachoKpis: {
            total: 0,
            unresolved: 0,
            unsigned: 0,
            critical: 0
        },
        alcoholKpis: {
            totalDrivers: 0,
            driversTested: 0,
            testingPercentage: 0,
            totalTests: 0,
            failed: 0,
            passRate: 100
        },
        subcontractedKpis: {
            profitPercentage: 0,
            jobsCount: 0,
            totalRevenue: 0,
            totalCost: 0
        },
        weeklyRevenueKpis: {
            thisWeekRevenue: 0,
            lastWeekRevenue: 0,
            percentageChange: 0
        },
        outstandingIssues: {
            total: 0,
            breakdown: {
                overdue_services: 0,
                outstanding_defects: 0,
                unsigned_tacho: 0,
                failed_alcohol_tests: 0
            },
            description: 'Items requiring immediate attention'
        }
    });

    async fetchWarehouseData(setIsLoading, setStorageCosts, setWarehouseData, setWarehouseCustomers, setOrders, logout, navigate, validSession ) {
        logger.log("Fetching warehouse data...");
        setIsLoading(true);

        // Helper function to validate response status
        const isSuccessful = (status) => [200, 201].includes(status);

        // Session check
        if (!validSession()) {
            setIsLoading(false);
            return this.showAlert({
                message: 'Session expired, please login again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/');
                        logger.log("Session expired, failed to fetch profiles");
                    },
                }],
            });
        }

        try {
            // Fetch all data concurrently
            const [
                warehouseResponse,
                warehouseCostsResponse,
                warehouseOrdersResponse
            ] = await Promise.all([
                fetch_all_lanes(),
                fetch_all_warehouse_costs(),
                fetchOrders()
            ]);

            // Process warehouse costs
            if (isSuccessful(warehouseCostsResponse.status)) {
                setStorageCosts(warehouseCostsResponse.data.data || []);
            }

            // Process warehouse data
            if (isSuccessful(warehouseResponse.status)) {
                const warehouseData = (warehouseResponse.data.data ?? [])
                    .slice()
                    .sort((a, b) =>
                        String(a.lane_id).localeCompare(
                            String(b.lane_id),
                            undefined,
                            { numeric: true, sensitivity: "base" }
                        )
                    );

                setWarehouseData(warehouseData);

                const customers = [
                    ...new Set(
                        warehouseData
                            .map(({ customer }) => customer)
                            .filter(c => c != null)
                    )
                ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "accent" }));

                setWarehouseCustomers(customers);
            }

            // Process orders
            if (isSuccessful(warehouseOrdersResponse.status)) {
                const ordersData = warehouseOrdersResponse.data.data || [];
                logger.log("Fetched Orders:", ordersData);
                setOrders(ordersData);
            }

        } catch (error) {
            logger.error("Error fetching data:", error);
            logout();
            navigate('/');
        } finally {
            // pause for 1 second to show loading spinner
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsLoading(false);
        }
    }

    // ============================================================================================================

    refreshOrders = async (setOrders) => {
        try {
            const warehouseOrdersResponse = await fetchOrders();
            if ([200, 201].includes(warehouseOrdersResponse.status)) {
                const ordersData = warehouseOrdersResponse.data.data || [];
                logger.log("Refreshed Orders:", ordersData);
                setOrders(ordersData);
            }
        } catch (error) {
            logger.error("Error refreshing orders:", error);
        }
    }

    // ============================================================================================================

    fetchDashboardData = async (setActualDashboardData) => {
        try {
            
            // Fetch real dashboard statistics from backend
            const response = await fetch_dashboard_statistics();
            
            if (response.status === 200 && response.data) {
                setActualDashboardData(response.data);
            } else {
                logger.error('Failed to fetch dashboard data:', response.message);
            }
            
        } catch (error) {
            logger.error('Error fetching dashboard data:', error);
        }
    };

    // ============================================================================================================

    fetchData = async (
        setIsLoading,
        setDriversList,
        setAdminList,
        setServiceList,
        setWarehouseStaff,
        setAllProfiles,
        setDefectsList,
        setDefectsFailed,
        setDefectsPassed,
        setDefectsRepaired,
        setOutstandingDefects,
        setAllUnits,
        setAllTrailers,
        setAllRigids,
        setAllMHE,
        setAllVehicles,
        setAllServiceRecords,
        setServiceOverdue,
        setServiceDue,
        setCustomers,
        setUniqueCustomers,
        setCustomerDeliveries,
        logout,
        navigate,
        validSession,
        isService = false,
        permissions = null,
    ) => {
        logger.log("Fetching data...");
        setIsLoading(true);

        const isSuccessful = (status) => [200, 201].includes(status);
        // fail-open: if no permissions object provided or if it is empty (still loading), allow all fetches
        const can = (key) => !permissions || Object.keys(permissions).length === 0 || permissions.is_dev || permissions[key];

        if (!validSession()) {
            setIsLoading(false);
            return this.showAlert({
                message: 'Session expired, please login again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/');
                        logger.log("Session expired, failed to fetch profiles");
                    },
                }],
            });
        }

        try {
            const [
                profilesResponse,
                defectsResponse,
                outstandingResponse,
                vehiclesResponse,
                serviceResponse,
                customersResponse,
                deliveriesResponse,
            ] = await Promise.all([
                (isService || !can('perm_users_view'))     ? Promise.resolve({ status: 204, data: [] })           : fetch_all_profiles(),
                (isService || !can('perm_defects_view'))   ? Promise.resolve({ status: 204, data: [] })           : fetch_todays_defects(),
                (isService || can('perm_defects_view'))     ? fetch_outstanding_defects()                          : Promise.resolve({ status: 204, data: [] }),
                (isService || can('perm_vehicles_view'))    ? fetch_all_vehicles()                                 : Promise.resolve({ status: 204, data: null }),
                (isService || can('perm_services_view'))    ? fetch_all_service_records()                          : Promise.resolve({ status: 204, data: [] }),
                (isService || !can('perm_customers_view')) ? Promise.resolve({ status: 204, data: { data: [] } }) : fetch_all_customers(),
                (isService || !can('perm_customers_view')) ? Promise.resolve({ status: 204, data: { data: [] } }) : fetch_all_customer_deliveries(),
            ]);

            if (isSuccessful(profilesResponse.status)) {
                const sortedProfiles = sortList(profilesResponse.data, 'name');
                const activeProfiles = sortedProfiles.filter(profile => profile.status === 'active');
                setDriversList(activeProfiles.filter(profile => profile.group === 'DRIVER'));
                setAdminList(activeProfiles.filter(profile => profile.group === 'ADMIN'));
                setServiceList(activeProfiles.filter(profile => profile.group === 'SERVICE'));
                setWarehouseStaff(activeProfiles.filter(profile => profile.group === 'WAREHOUSE'));
                setAllProfiles(activeProfiles);
            }

            if (isSuccessful(defectsResponse.status)) {
                setDefectsList(defectsResponse.data);

                const failedDrivers = new Set(defectsResponse.data.filter(d => d.check_status === 'failed').map(d => d.driver_id));
                const passedDrivers = new Set(defectsResponse.data.filter(d => d.check_status === 'passed').map(d => d.driver_id));
                const repairedDrivers = defectsResponse.data.filter(d => d.check_status === 'repaired').map(d => d.driver_id);

                setDefectsFailed([...failedDrivers]);
                setDefectsPassed([...passedDrivers].filter(d => !failedDrivers.has(d)));
                setDefectsRepaired(repairedDrivers);
            }

            setOutstandingDefects(isSuccessful(outstandingResponse.status) ? outstandingResponse.data : []);

            if (vehiclesResponse.data) {
                const activeVehicles = sortList(vehiclesResponse.data.filter(v => v.status === 'active'), 'reg');
                setAllUnits(activeVehicles.filter(v => v.type === 'UNIT'));
                // setAllTrailers(activeVehicles.filter(v => v.type === 'TRAILER'));
                setAllRigids(activeVehicles.filter(v => v.type === 'RIGID'));
                setAllMHE(activeVehicles.filter(v => v.type === 'MHE'));
                setAllVehicles(activeVehicles);

                // sort trailers (moffett / doubledeck) with proper numeric sorting
                const allTrailers = activeVehicles.filter(v => v.type === 'TRAILER');
                const moffettTrailers = allTrailers.filter(v => v.reg.slice(-1) === 'M');
                const doubleDeckTrailers = allTrailers.filter(v => v.reg.slice(-1) === 'D');
                const otherTrailers = allTrailers.filter(v => !moffettTrailers.includes(v) && !doubleDeckTrailers.includes(v));
                
                // Sort each category with natural numeric sorting
                const sortNaturally = (arr) => arr.sort((a, b) => 
                    a.reg.localeCompare(b.reg, undefined, { numeric: true, sensitivity: 'base' })
                );
                
                const sortedTrailers = [
                    ...sortNaturally(otherTrailers), 
                    ...sortNaturally(moffettTrailers), 
                    ...sortNaturally(doubleDeckTrailers)
                ];
                setAllTrailers(sortedTrailers);
            }

            if (isSuccessful(serviceResponse.status)) {
                setAllServiceRecords(serviceResponse.data);
                const overdue = [];
                const due = [];

                const serviceRecords = Array.isArray(serviceResponse.data) ? serviceResponse.data : (serviceResponse.data?.data || []);
                for (const record of serviceRecords) {
                    const today = new Date();
                    const checks = [
                        { key: 'service', completedKey: 'service_completed' },
                        { key: 'brakes', completedKey: 'brakes_completed' },
                        { key: 'mot', completedKey: 'mot_completed' },
                        { key: 'tacho', completedKey: 'tacho_completed' },
                        { key: 'loler', completedKey: 'loler_completed' },
                    ];
                    if (!record.vor) {
                        for (let { key, completedKey } of checks) {
                            if (record[key] && !record[completedKey]) {
                                const dueDate = new Date(record.date);
                                const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
                                if (dueDate < today) {
                                    overdue.push(record.vehicle_id);
                                } else if (diffDays < 7) {
                                    due.push(record.vehicle_id);
                                }
                            }
                        }
                    }
                }
                setServiceOverdue(overdue);
                setServiceDue(due);
            }

            if (isSuccessful(customersResponse.status)) {
                setCustomers(customersResponse.data.data|| []);
                if (customersResponse.data.data && customersResponse.data.data.length > 0) {
                    const orderedById = customersResponse.data.data.sort((a, b) => a.id - b.id);
                    let uniqueCustomers = new Map();
                    orderedById.forEach(item => {
                        if (!uniqueCustomers.has(item['company'])) {
                            uniqueCustomers.set(item['company'], item);
                        }
                    });
                    const orderedByCompany = [...uniqueCustomers.values()].sort((a, b) => a.company.localeCompare(b.company));
                    setUniqueCustomers(orderedByCompany);
                }
            }

            if (isSuccessful(deliveriesResponse.status)) {
                setCustomerDeliveries(deliveriesResponse.data.data || []);
            }

        } catch (error) {
            logger.error("Error fetching data:", error);
            logout();
            navigate('/');
        } finally {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsLoading(false);
        }
    }

    fetchHolidayRequestsCount = async (setHolidayRequestsCount) => {
        try {
            const result = await fetch_pending_holiday_requests_count();
            if (result.success) {
                setHolidayRequestsCount(result.count || 0);
            } else {
                logger.error('Failed to fetch holiday requests count:', result.error);
                setHolidayRequestsCount(0);
            }
        } catch (error) {
            logger.error('Error fetching holiday requests count:', error);
            setHolidayRequestsCount(0);
        }
    };

    fetchHolidayRequests = async (setPendingHolidayRequests, setHolidayRequestsCount) => {
        try {
            const result = await fetch_pending_holiday_requests();
            if (result.success) {
                setPendingHolidayRequests(result.data || []);
                setHolidayRequestsCount(result.data?.length || 0);
            } else {
                logger.error('Failed to fetch holiday requests:', result.error);
                setPendingHolidayRequests([]);
                setHolidayRequestsCount(0);
            }
        } catch (error) {
            logger.error('Error fetching holiday requests:', error);
            setPendingHolidayRequests([]);
            setHolidayRequestsCount(0);
        }
    };
}